/**
 * Kachel je Apartment (Board „Preisautomatik · Übersicht“): Name, Strategie aus dem Profil, Modus,
 * 14 Tage bestätigt gegen Ziel, Status aus Abweichung und Anfragen, Berechnet · Bestätigt · Belegt,
 * Konfiguration, nächster Lauf und manuell geschützte Tage; Wege in Einstellungen und Monat.
 * Ein Apartment ohne Automatik behält seine Fakten und sagt, wie man sie einschaltet.
 */
import { Link } from '@tanstack/react-router';
import type { DynamicPricingApartmentOverview } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { ModeChip } from '@/features/availability-distribution/automation/AutomationMarks';
import { OverviewSparkline } from './OverviewSparkline';
import { tileStatus, type Tone } from './overviewModel';

const TONE: Record<Tone, string> = {
  green: 'bg-forest-soft text-forest-deep',
  amber: 'bg-amber-tint text-amber-ink',
  red: 'bg-sealwax-tint text-sealwax',
  neutral: 'bg-card-alt text-ink-soft',
};

interface Props {
  apartment: DynamicPricingApartmentOverview;
  strategy: string;
  /** Workspace gestoppt, global gestoppt oder ohne Steuerung: es wird nichts berechnet. */
  halted: boolean;
  time: (iso: string | null) => string;
  range: string;
}

export function OverviewTile({ apartment, strategy, halted, time, range }: Props) {
  const active = apartment.mode !== 'Off';
  const status = halted
    ? { tone: 'red' as const, text: 'Gestoppt – Werte bleiben, wie sie zuletzt bestätigt wurden' }
    : tileStatus(apartment);
  const { cockpit } = apartment;
  const next =
    active && !halted && apartment.nextScheduledRunAtUtc
      ? ` · nächster Lauf ${time(apartment.nextScheduledRunAtUtc)}`
      : active
        ? ' · kein Lauf geplant'
        : '';
  return (
    <article
      className={cn(
        'rounded-hero flex flex-col gap-3 border p-[18px]',
        active ? 'bg-card' : 'bg-paper border-dashed',
        active && status.tone === 'red' ? 'border-sealwax/40' : 'border-hairline',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="flex min-w-0 grow flex-col">
          <span className="truncate font-serif text-lg font-bold">{apartment.name}</span>
          <span className="text-ink-soft text-xs">{strategy}</span>
        </span>
        <ModeChip mode={apartment.mode} />
      </div>
      {active ? (
        <>
          <OverviewSparkline days={cockpit.days} range={range} currency={apartment.currency} />
          <span
            className={cn(
              'rounded-control self-start px-3 py-1 text-xs font-bold',
              TONE[status.tone],
            )}
          >
            {status.text}
          </span>
        </>
      ) : (
        <span className="text-ink-soft text-[13px]">
          Die Preise setzt ihr hier von Hand. Einschalten lässt sich die Automatik in den
          Einstellungen.
        </span>
      )}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <Fact label="Berechnet" value={time(apartment.lastRun?.completedAtUtc ?? null)} />
        <Fact label="Bestätigt" value={time(cockpit.lastConfirmedAtUtc)} />
        <Fact label="Belegt" value={`${cockpit.bookedDays} von ${cockpit.days.length || 14}`} />
      </div>
      <span className="text-ink-soft text-xs">
        Konfiguration v{apartment.configurationVersionNumber}
        {next}
        {cockpit.manuallyProtectedDays > 0 &&
          ` · ${cockpit.manuallyProtectedDays} manuell geschützt`}
      </span>
      <div className="mt-auto flex flex-wrap gap-2">
        <Link
          to="/dynamic-pricing/configuration"
          search={{ apartment: apartment.propertyId }}
          className="rounded-pill bg-card-alt text-ink flex h-11 items-center px-3.5 text-[13px] font-bold"
        >
          Einstellungen
        </Link>
        {active && (
          <Link
            to="/availability-distribution"
            search={{ apartment: apartment.propertyId }}
            className="rounded-pill bg-forest flex h-11 items-center px-3.5 text-[13px] font-bold text-white"
          >
            Im Kalender
          </Link>
        )}
      </div>
    </article>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex flex-col">
      <span className="text-ink-soft">{label}</span>
      <b>{value}</b>
    </span>
  );
}
